/**
 * 단위 변환 유틸 (탄소 배출량 등)
 * - kgCO2e ↔ tCO2e 등 확장 가능한 변환 테이블 기반
 * - 반환값은 기본적으로 toLocaleString 적용된 문자열
 */

// 변환 계수: fromUnit -> toUnit 일 때 곱할 값 (value * factor)
const CONVERSION_MAP = {
  kgCO2e: { tCO2e: 0.001 },
  tCO2e: { kgCO2e: 1000 },
};

const DEFAULT_LOCALE = 'ko-KR';
/** 기본 소수점 자리수 (포맷 시) */
const DEFAULT_FRACTION_DIGITS = 2;

/**
 * 단위 변환 (포맷된 문자열 반환)
 * @param {number} value - 변환할 값
 * @param {string} fromUnit - 원래 단위 (예: 'kgCO2e')
 * @param {string} toUnit - 목표 단위 (예: 'tCO2e')
 * @param {Object} [options]
 * @param {string} [options.locale] - toLocaleString locale (기본: 'ko-KR')
 * @param {number} [options.maxFractionDigits] - 소수점 최대 자리수
 * @param {number} [options.minFractionDigits] - 소수점 최소 자리수
 * @returns {string} toLocaleString 적용된 변환 결과 문자열
 *
 * @example
 * convertUnit(1500, 'kgCO2e', 'tCO2e')           // "1.5"
 * convertUnit(1500, 'kgCO2e', 'tCO2e', { maxFractionDigits: 2 })  // "1.5"
 */
export function convertUnit(value, fromUnit, toUnit, options = {}) {
  let num = convertUnitRaw(value, fromUnit, toUnit);
  const locale = options.locale ?? DEFAULT_LOCALE;
  const formatOptions = {};
  if (options.maxFractionDigits !== undefined) formatOptions.maximumFractionDigits = options.maxFractionDigits;
  if (options.minFractionDigits !== undefined) formatOptions.minimumFractionDigits = options.minFractionDigits;
  // 소수점 0자리면 반드시 정수로 반올림 후 포맷
  if (options.maxFractionDigits === 0) num = Math.round(num);
  return Object.keys(formatOptions).length
    ? num.toLocaleString(locale, formatOptions)
    : num.toLocaleString(locale);
}

/**
 * 단위 변환 (숫자만 반환, 추가 연산용)
 * @param {number} value - 변환할 값
 * @param {string} fromUnit - 원래 단위
 * @param {string} toUnit - 목표 단위
 * @returns {number}
 */
export function convertUnitRaw(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  const factor = CONVERSION_MAP[fromUnit]?.[toUnit];
  if (factor == null) {
    console.warn(`[convertUnit] 지원하지 않는 변환: ${fromUnit} -> ${toUnit}`);
    return value;
  }
  return value * factor;
}

/**
 * kgCO2e -> tCO2e 변환 (편의 함수)
 * @param {number} kgCO2e
 * @param {Object} [options] - convertUnit과 동일
 * @returns {string}
 */
export function kgCO2eToTCO2e(kgCO2e, options = {}) {
  return convertUnit(kgCO2e, 'kgCO2e', 'tCO2e', options);
}

/**
 * tCO2e -> kgCO2e 변환 (편의 함수)
 * @param {number} tCO2e
 * @param {Object} [options] - convertUnit과 동일
 * @returns {string}
 */
export function tCO2eToKgCO2e(tCO2e, options = {}) {
  return convertUnit(tCO2e, 'tCO2e', 'kgCO2e', options);
}

const KG_TO_T_THRESHOLD = 1000;

/**
 * kgCO2e 값을 받아 1000 이상이면 tCO2e로 자동 변환 후 toLocaleString하여 반환
 * @param {number} value - 탄소 배출량 값
 * @param {string} [unit='kgCO2e'] - 입력 단위 (현재는 'kgCO2e'만 지원)
 * @param {Object} [options] - convertUnit과 동일 (locale, maxFractionDigits 등)
 * @returns {{ formatted: string, unit: 'kgCO2e'|'tCO2e' }} 포맷된 문자열과 사용된 단위 (UI에서 "formatted + unit" 표시용)
 *
 * @example
 * formatCarbonAuto(500)    // { formatted: "500", unit: "kgCO2e" }
 * formatCarbonAuto(1500)  // { formatted: "1.5", unit: "tCO2e" }
 */
function normalizeFractionOptions(options) {
  const opts = { ...options };
  if (opts.maxFractionDigits === undefined) opts.maxFractionDigits = DEFAULT_FRACTION_DIGITS;
  return opts;
}

export function formatCarbonAuto(value, unit = 'kgCO2e', options = {}) {
  const opts = normalizeFractionOptions(options);
  const isIntegerOnly = opts.maxFractionDigits === 0;
  if (unit !== 'kgCO2e') {
    const num = Number(value);
    const n = Number.isFinite(num) ? (isIntegerOnly ? Math.round(num) : num) : num;
    const formatOpts = { maximumFractionDigits: opts.maxFractionDigits };
    if (opts.minFractionDigits !== undefined) formatOpts.minimumFractionDigits = opts.minFractionDigits;
    const formatted = Number.isFinite(n)
      ? n.toLocaleString(opts.locale ?? DEFAULT_LOCALE, formatOpts)
      : String(value);
    return { formatted, unit };
  }
  let num = Number(value);
  if (!Number.isFinite(num)) return { formatted: String(value), unit: 'kgCO2e' };
  if (isIntegerOnly) num = Math.round(num);
  if (num >= KG_TO_T_THRESHOLD) {
    return {
      formatted: convertUnit(num, 'kgCO2e', 'tCO2e', opts),
      unit: 'tCO2e',
    };
  }
  const locale = opts.locale ?? DEFAULT_LOCALE;
  const formatOptions = { maximumFractionDigits: opts.maxFractionDigits };
  if (opts.minFractionDigits !== undefined) formatOptions.minimumFractionDigits = opts.minFractionDigits;
  const formatted = num.toLocaleString(locale, formatOptions);
  return { formatted, unit: 'kgCO2e' };
}

/**
 * kgCO2e 값을 1000 기준으로 자동 변환한 뒤, 포맷된 숫자 + 단위를 한 문자열로 반환 (null/비유효 시 "—")
 * UI에서 단위를 하드코딩하지 않고 한 번에 표시할 때 사용
 * @param {number|null|undefined} value - 탄소 배출량 값
 * @param {string} [unit='kgCO2e'] - 입력 단위
 * @param {Object|number} [optionsOrDecimal] - 옵션 객체 또는 소수점 자리수(숫자). 기본 2
 *   - 숫자면 maxFractionDigits/minFractionDigits 로 사용 (0 = 자연수만)
 *   - 객체면 formatCarbonAuto와 동일 (maxFractionDigits, minFractionDigits, locale 등)
 * @returns {string} "1.5 tCO2e", "500 kgCO2e", "—" 등
 *
 * @example
 * formatCarbonAutoWithUnit(1500)           // "1.5 tCO2e" (기본 소수점 2자리)
 * formatCarbonAutoWithUnit(1500, 'kgCO2e', 0)  // "2 tCO2e" (자연수만)
 * formatCarbonAutoWithUnit(null)          // "—"
 */
function parseDecimalParam(optionsOrDecimal) {
  if (typeof optionsOrDecimal === 'number' && Number.isInteger(optionsOrDecimal)) return optionsOrDecimal;
  if (typeof optionsOrDecimal === 'string' && optionsOrDecimal !== '' && !isNaN(Number(optionsOrDecimal)))
    return Number(optionsOrDecimal);
  return undefined;
}

export function formatCarbonAutoWithUnit(value, unit = 'kgCO2e', optionsOrDecimal = {}) {
  if (value == null || (typeof value === 'number' && !Number.isFinite(value))) return '—';
  const decimalPlaces = parseDecimalParam(optionsOrDecimal);
  const options =
    decimalPlaces !== undefined
      ? { maxFractionDigits: decimalPlaces, minFractionDigits: decimalPlaces }
      : optionsOrDecimal;
  const { formatted, unit: u } = formatCarbonAuto(value, unit, options);
  return `${formatted} ${u}`;
}

/**
 * formatCarbonAuto와 동일한 변환/포맷을 적용하되, 단위 없이 숫자만 반환 (null/비유효 시 "—")
 * @param {number|null|undefined} value
 * @param {string} [unit='kgCO2e']
 * @param {Object|number} [optionsOrDecimal] - formatCarbonAutoWithUnit과 동일
 * @returns {string} "1.5", "500", "—" 등
 */
export function formatCarbonAutoOnly(value, unit = 'kgCO2e', optionsOrDecimal = {}) {
  if (value == null || (typeof value === 'number' && !Number.isFinite(value))) return '—';
  const decimalPlaces = parseDecimalParam(optionsOrDecimal);
  const options =
    decimalPlaces !== undefined
      ? { maxFractionDigits: decimalPlaces, minFractionDigits: decimalPlaces }
      : optionsOrDecimal;
  const { formatted } = formatCarbonAuto(value, unit, options);
  return formatted;
}
